import React, {PropsWithChildren, useContext} from "react"
import {
	Claims,
	useIsBankUser,
	useIsOrgUser,
	useIsPartnerUser,
	useIsUnitAdminOrgAdminBankOperationsOnly,
	useIsUnitAdminReadonlyUser,
	useIsUnitAdminUser,
	useIsUnitUser,
} from "../../services/auth"
import {claimsContext} from "../../services/claims"
import {isOrgManageResubmitApplication} from "../../resources/orgPolicy"
import {hasPermission, useIsAllowedToEvaluate} from "../../services/permission"
import {Assignee} from "../../resources/application"
import {DocumentableApplication} from "../../resources/documentable"
import {useDocumentableIsAllowedToEvaluate, documentableHasPermission} from "../../services/documentablePermission"

export function UnitUserOnly({children}: PropsWithChildren<{}>) {
	if (useIsUnitUser()) return <>{children}</>
	return null
}

export function UnitAdminOnly({children, claims}: PropsWithChildren<{claims?: Claims}>) {
	if (useIsUnitAdminUser(claims)) return <>{children}</>
	return null
}

export function UnitAdminReadOnly({children, claims}: PropsWithChildren<{claims?: Claims}>) {
	if (useIsUnitAdminReadonlyUser(claims)) return <>{children}</>
	return null
}

export function UnitAdminOrgAdminBankOperationsOnly({children}: PropsWithChildren<{}>) {
	const isUnitAdminOrgAdminBankOperationsOnly = useIsUnitAdminOrgAdminBankOperationsOnly()

	if (isUnitAdminOrgAdminBankOperationsOnly) return <>{children}</>
	return null
}

export function UnitOrOrgOrPartnerOnlyUser({children}: PropsWithChildren<{}>) {
	const isUnitUser = useIsUnitUser()
	const isOrgUser = useIsOrgUser()
	const isPartnerUser = useIsPartnerUser()

	if (isUnitUser || isOrgUser || isPartnerUser) return <>{children}</>
	return null
}

export function UnitOrBankUserOnly({children}: PropsWithChildren<{}>) {
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()

	if (isUnitUser || isBankUser) return <>{children}</>
	return null
}

export function UnitOrOrgManageResubmitOrBankOnly({children}: PropsWithChildren<{}>) {
	const isUnit = useIsUnitUser()
	const isBank = useIsBankUser()
	const orgId = useContext(claimsContext)?.orgId
	const isOrg = useIsOrgUser()
	const canViewApplicationDecision = hasPermission("application.applicationDecision", "get")

	const isOrgManageResubmit = canViewApplicationDecision && isOrg && !!orgId && isOrgManageResubmitApplication(orgId)
	const isManageResubmit = isUnit || isBank || isOrgManageResubmit

	return isManageResubmit ? <>{children}</> : null
}

export function useIsAllowedToPreviewApprovalRejectInformation() {
	return hasPermission("application.applicationDecision", "get")
}

export function useCanViewProductApplicationDecision() {
	return hasPermission("productApplication.applicationDecision", "get")
}

export function AllowedToViewEvaluation({children, applicationOrgId}: PropsWithChildren<{applicationOrgId: string}>) {
	if (useIsAllowedToEvaluate(applicationOrgId, false)) return <>{children}</>
	return null
}

export function AllowedToEvaluateUserOnly({
	children,
	applicationOrgId,
	assignee,
}: PropsWithChildren<{applicationOrgId: string; assignee: Assignee}>) {
	if (useIsAllowedToEvaluate(applicationOrgId, true, assignee)) return <>{children}</>
	return null
}

export function ProductApplicationAllowedToViewEvaluation({children}: PropsWithChildren<{}>) {
	const isUnitUser = useIsUnitUser()
	if (isUnitUser) return <>{children}</>
	return null
}

export function DocumentableAllowedToEvaluateUserOnly({
	children,
	application,
}: PropsWithChildren<{application: DocumentableApplication}>) {
	if (useDocumentableIsAllowedToEvaluate(application, true)) return <>{children}</>
	return null
}

export function DocumentableHasPermission({
	children,
	application,
	resource,
	action,
}: PropsWithChildren<{application: DocumentableApplication; resource: string; action: string}>) {
	return documentableHasPermission(application, resource, action) ? <>{children}</> : null
}
